/**
 * Vehicle DNA — calls the `vehicleDNA` Cloud Function and returns a
 * structured analysis of the user's car (stats + insights vs similar
 * vehicles in Israel).
 *
 * The server uses Claude Sonnet 4.5 to generate plausible Israel-2026
 * estimates based on the model. Values aren't pulled from real-time
 * market data — they're "expert estimates" Claude knows about — but
 * they're scoped tightly to the user's specific make/model/year/etc.
 *
 * @example
 *   const dna = await getDNA();
 *   // → { summary, stats: [...], insights: [...] }
 */

import { auth, ref, getFirebase } from '../../core/firebase.js';
import { LocalState } from '../../core/localState.js';
import { Firebase } from '../../app/config.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('vehicle-dna');

/**
 * @typedef {Object} DNAStat
 * @property {string} label
 * @property {string} value
 * @property {string} [context]
 */

/**
 * @typedef {Object} DNAInsight
 * @property {string} icon
 * @property {string} title
 * @property {string} text
 */

/**
 * @typedef {Object} DNAResult
 * @property {string} summary
 * @property {DNAStat[]} stats
 * @property {DNAInsight[]} insights
 */

/**
 * Build the vehicle context for the DNA call. Mirrors the shape used by
 * the AI chat, but the DNA only needs static gov.il data (no live license
 * info needed).
 *
 * @returns {Promise<any|null>}
 */
async function buildVehicleContextForDNA() {
  const plate = String(LocalState.plate || '').replace(/\D/g, '');
  if (!plate) return null;
  try {
    const govSnap = await ref(`vehicles/${plate}/gov/vehicle`).once('value');
    const gov = govSnap.val() || {};
    return {
      plate,
      year: gov.shnat_yitzur || LocalState.carYear || '',
      make: gov.tozeret_nm || '',
      model: gov.kinuy_mishari || gov.degem_nm || '',
      fuel: gov.sug_delek_nm || '',
      engine: gov.tnufat_ha_manoa || gov['tnufat_ha-manoa'] || '',
      color: gov.tzeva_rechev || '',
      trimLevel: gov.ramat_gimur || '',
      ownersCount: (typeof gov.baalut === 'number') ? gov.baalut
                 : (typeof gov.baalut === 'string' && /^\d+$/.test(gov.baalut)) ? Number(gov.baalut)
                 : null,
      firstRegistration: gov.moed_aliya_lakvish || '',
    };
  } catch (err) {
    log.warn('buildVehicleContextForDNA failed:', err?.message);
    return null;
  }
}

/**
 * Fetch the DNA card for the active vehicle. Throws if no plate / not
 * authenticated / quota exceeded.
 *
 * @returns {Promise<{ dna: DNAResult, quotaLeft: number }>}
 */
export async function getDNA() {
  const uid = auth().currentUser?.uid;
  if (!uid) throw new Error('not authenticated');

  const vehicleContext = await buildVehicleContextForDNA();
  if (!vehicleContext || !vehicleContext.make || !vehicleContext.model) {
    throw new Error('no vehicle data');
  }

  const fb = /** @type {any} */ (getFirebase());
  const callable = fb.app().functions(Firebase.functionsRegion).httpsCallable('vehicleDNA');
  const result = await callable({ vehicleContext });
  const data = result?.data || {};
  if (!data.ok || !data.dna) throw new Error('DNA call returned empty');
  return { dna: data.dna, quotaLeft: Number(data.quotaLeft || 0) };
}
